import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import DisplayConsent from "../../components/organisms/Home/InscriptionJourney/DisplayConsent";
import DisplayHasAccount from "../../components/organisms/Home/PublicJourney/DisplayHasAccount";
import DisplayStudyDetails from "../../components/organisms/Home/PublicJourney/DisplayStudyDetails";
import DisplayInclusion from "../../components/organisms/Home/InscriptionJourney/DisplayInclusion";
import DisplayLogin from "../../components/organisms/Home/LoginJourney/DisplayLogin";
import DisplayProfileSelector from "../../components/organisms/Home/PublicJourney/DisplayProfileSelector";
import { isNoSignInMethod, ProfileType } from "../../types/profile";
import {
  postConsentRequestType,
  PostUserType,
  UserStatus,
  WelcomeType,
  WelcompeStepEnum,
} from "../../types/welcome";
import OnBoarding from "../../components/organisms/Home/InscriptionJourney/OnBoarding/OnBoarding";
import DisplayInscription from "../../components/organisms/Home/InscriptionJourney/DisplayInscription";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import StratificationPage from "../../components/organisms/Home/InscriptionJourney/StratificationPage";
import DisplaySites from "../../components/organisms/Home/InscriptionJourney/DisplaySites";
import DisplayNotIncluded from "../../components/organisms/Home/InscriptionJourney/DisplayNotIncluded";
import { useTranslation } from "react-i18next";
import { postConsent, postConsentByStratificationQuestions, postNewUser } from "../../services/user.service";
import { useAuth } from "../../contexts/AuthContext";
import DisplayRegistredSuccessfully from "../../components/organisms/Home/InscriptionJourney/DisplayRegistredSuccessfully";
import { useConfig } from "../../contexts/ConfigContext";
import { getWelcomeInfo } from "../../services/weclome.service";
import useQuery from "../../hooks/useQuery";
import Info from "../../components/atoms/ErrorComponent";
import useToast from "../../hooks/useToast";
import { useFetch } from "../../hooks/useFetch";
import { getStudySlug } from "../../helper/study";
import DisplayInvitation from "../../components/organisms/Home/InscriptionJourney/DisplayInvitation";
import DisplayInitIdentification from "../../components/organisms/Home/InscriptionJourney/DisplayInitIdentification";
import { RoleEnum } from "../../types/invitation.types";
import ResetPwdModal from "../Profile/ResetPasswordModal";
import { getStudyLangs } from "../../services/study.service";
import { debug } from "console";
import { on } from "events";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { ResonanceLayoutProvider } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";

const DEFAULT_DISPLAY: WelcompeStepEnum = WelcompeStepEnum.HOME;

interface HomeContextType {
  welcomeData: WelcomeType;
  selectedRole: ProfileType;
  currentStep: WelcompeStepEnum;
  previousStep: WelcompeStepEnum;
  setCurrentStep(step: WelcompeStepEnum): void;
  goPrevious(): void;
  createUser(role: ProfileType): void;
}

const NO_PROIVDER_MESSAGE = "HomeContext - no provider";

const HomeContextDefaultValue: HomeContextType = {
  welcomeData: undefined,
  selectedRole: undefined,
  currentStep: undefined,
  previousStep: undefined,
  setCurrentStep: () => console.log(NO_PROIVDER_MESSAGE),
  goPrevious: () => console.log(NO_PROIVDER_MESSAGE),
  createUser: () => console.log(NO_PROIVDER_MESSAGE),
};

const HomeContext = createContext<HomeContextType>(HomeContextDefaultValue);

const HomeContextProvider = HomeContext.Provider;

export const useHomeContext = () => useContext(HomeContext);

const HomeContent = ({ entrySegment }: { entrySegment?: string } = {}): ReactElement => {

  const query = useQuery();
  const invitation_token = query.get("invitation");
  const forget_password_token = query.get("forget_password_token");
  const login = query.get("login");
  const channel = query.get("channel");

  const [steps, setSteps] = useState<WelcompeStepEnum[]>([DEFAULT_DISPLAY]);
  const [isResetPwdModalOpen, setIsResetPwdModalOpen] = useState(!!forget_password_token);
  const [loading, setLoading] = useState<boolean>(false);
  const setCurrentStep = useCallback(
    (step: WelcompeStepEnum) => {
      setSteps([...steps, step]);
    },
    [steps]
  );

  const currentStep = useMemo(() => steps[steps.length - 1], [steps]);
  const previousStep = useMemo(() => steps.length > 1 && steps[steps.length - 2], [steps]);

  const goPrevious = useCallback(
    () => steps.length > 1 && setSteps(steps.slice(0, -1)),
    [steps]
  );

  const [inclusionContentId, setInclusionContentId] = useState<number | null>(
    null
  );

  const [userInfo, setUserInfo] = useState<any>();
  const [stratification, setStratification] = useState<
    postConsentRequestType["stratification_questions"] | null
  >(null);
  const [consentId, setConsentId] = useState<number>();
  const [consent, setConsent] = useState<any>();
  const { setRole, selectedRole, study } = useConfig();

  const slug = useMemo(getStudySlug, []);
  //const slug = "mentalplus";

  // console.log("slug:", slug);
  const has_consent = selectedRole?.consent?.has_consent;
  const present = useToast();
  const { getCurrent, endSession, isInitialized, startSession, user } =
    useAuth();

  const { t, i18n } = useTranslation();

  const {
    isError,
    isLoading,
    data: welcomeData,
  } = useFetch<WelcomeType>(
    "welcome",
    getWelcomeInfo,
    { slug, lang: i18n.language, invitation_token },
    {
      enabled: Boolean(slug && i18n.language),
    }
  );
  
  useEffect(() => {
    const userPreferredLang = localStorage.getItem("brows_lang")
    if (userPreferredLang) {
      getStudyLangs(study.id).then(({ data }) => {
        i18n.changeLanguage(userPreferredLang);
      }).catch()
    } else {
      getStudyLangs(study.id).then(({ data }) => {
        i18n.changeLanguage(data.primary_lang);
      }).catch()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onStratification = useCallback(
    async (stratification, consent) => {
      try {
        const User = user ? user : consent.data.userAuth
        const userMock = { ...consent.data };
        delete consent.data
        const body: postConsentRequestType = {
          user: userInfo ? userInfo.user : userMock.user,
          identification_questions: userInfo ? userInfo.identification_questions : userMock.identification_questions,
          consent: consent,
          stratification_questions: stratification,
        };
        setLoading(true)
        await postConsent(welcomeData.id, User.id, body);
        await getCurrent();

        if (
          welcomeData.invitations?.to_user.user_status ===
          UserStatus.INCLUSION_EN_ATTENTE_DE_VALIDATION
        ) {
          setCurrentStep(WelcompeStepEnum.INVITATION);
          return;
        }

        if (selectedRole.attributes.is_site_requested) {
          setCurrentStep(WelcompeStepEnum.SITES);
        } else {
          setCurrentStep(WelcompeStepEnum.REGISTRED_SUCCESSFULLY);
        }
      } catch (e) {
        if (e?.response?.status === 409) {
          setConsentId(null);
          setStratification((current) =>
            Array.isArray(current) ? [...current] : []
          );
          present(
            "Votre reservation a expire. Le consentement est en cours de rechargement."
          );
          return;
        }

        present(e?.response?.data?.message);
      }
      finally{
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userInfo, selectedRole, user, welcomeData]
  );

  const handlePreStratification = useCallback(
    (cons) => {
      switch (selectedRole.name) {
        case RoleEnum.SUBJECT:
          selectedRole.attributes.has_stratification ? setCurrentStep(WelcompeStepEnum.STRATIFICATION) : onStratification([], cons);
          break;
        default:
          onStratification([], cons);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRole, userInfo, welcomeData]
  );

  const handleNextInscription = useCallback(async (data) => {
    if(selectedRole.attributes.has_stratification){
      setCurrentStep(WelcompeStepEnum.STRATIFICATION);
    }
    else {
      setStratification([]);
      // if (has_consent) {
      //   setCurrentStep(WelcompeStepEnum.CONSENT);
      // } else {
      //   const currentConsent = {
      //     id: selectedRole.consent.id,
      //     is_validated: true,
      //     validation_date: new Date().toISOString(),
      //     version: 1,
      //     signature: "signature",
      //     data: data
      //   }
      //   setConsent(currentConsent)
      //   onStratification([], currentConsent);
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has_consent, selectedRole]);

  const createUser = async (role: ProfileType): Promise<void> => {
    try {
      const userInfos: PostUserType = {
        role_id: role?.id,
          lang: i18n.resolvedLanguage ?? i18n.language,
      };

      const invitation = query.get("invitation");

      if (invitation) {
        userInfos.invitation_token = invitation;
      }
      
      console.log("welcomeData.id : " + welcomeData.id)
      console.log("userInfos.role_id : " + userInfos.role_id)
      console.log("userInfos.lang : " + userInfos.lang)

      const { data } = await postNewUser(welcomeData.id, userInfos);
      await startSession(data.token);
      setCurrentStep(WelcompeStepEnum.ON_BOARDING);
    } catch (e) {
      console.error(e);
      present(e?.response?.data?.message);
    }
  };

  const onProfileSelection = (
    newRole: ProfileType,
    shouldAdvanceToHasAccount: boolean = true
  ): void => {
    setRole(newRole);
    if (isNoSignInMethod(newRole.access.signInMode)) {
      createUser(newRole);
    } else if (shouldAdvanceToHasAccount) {
      setCurrentStep(WelcompeStepEnum.HAS_ACCOUNT);
    }
  };

  useEffect(() => {
    (async () => {
      if (isInitialized && welcomeData && currentStep === WelcompeStepEnum.HOME) {
        if (user) {
          await endSession();
        }
        if (welcomeData.roles.length > 0) {
          // Pre-select the role matching the entry URL segment (e.g. /investigator, /parent).
          // The study slug and unmatched/typo segments fall back to the subject so the
          // bare landing ("/", "/mentalplus") always shows the subject experience.
          const roles = welcomeData.roles;
          const matchByEntry =
            entrySegment && entrySegment !== slug
              ? roles.find((r) => r.url_slug && r.url_slug === entrySegment && r.id != null)
              : undefined;
          const targetRole =
            matchByEntry ??
            roles.find((r) => r.name === RoleEnum.SUBJECT && r.id != null) ??
            roles[0];
          if (targetRole?.access) {
            onProfileSelection(targetRole, false);
          }
        }
      }
    })();
  }, [isInitialized, welcomeData, entrySegment]);

  useEffect(
    function () {
      const profileId = welcomeData?.invitations?.to_user?.profile;
      if (profileId && welcomeData) {
        const role = welcomeData.roles.find((role) => role.id === profileId);
        setRole(role);
      }
      else if (selectedRole && welcomeData) {
        const selectedRoleData = welcomeData.roles.find((role) => role.id === selectedRole.id);
        setRole(selectedRoleData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [welcomeData]
  );

  useEffect(() => {
  if (!Array.isArray(stratification)) return;
  if (!selectedRole || !study) return;

  // If the user's role doesn't have consent, skip directly
  if (!selectedRole?.consent?.has_consent) {
    const currentConsent = {
      id: selectedRole?.consent?.id,
      is_validated: true,
      validation_date: new Date().toISOString(),
      version: 1,
      signature: "signature",
      data: userInfo
    };
    onStratification(stratification, currentConsent);
    return;
  }

  (async function fetchConsentId() {
    try {
      setLoading(true);

      const response = await postConsentByStratificationQuestions({
        studyId: study.id,
        roleId: selectedRole.id, // ok to keep if your API accepts it (even if unused)
        stratification_questions: stratification,
      });

      const newConsentId = response.data?.consent_id; // number | null
      console.log("Consent ID: ", newConsentId);

      // ✅ if backend couldn't find any consent
      if (!newConsentId) {
        // choose your behavior:
        // 1) fallback to role consent id
        setConsentId(selectedRole.consent?.id);

        // or 2) block with error UI / toast
        // present("No consent found for this stratification");
        // return;

      } else {
        setConsentId(newConsentId);
      }

      setCurrentStep(WelcompeStepEnum.CONSENT);
    } catch (error) {
      console.error("Error fetching dynamic consent ID:", error);
    } finally {
      setLoading(false);
    }
  })();
}, [stratification, selectedRole, study]);

  

  const stepContent = useMemo(
    function () {
      const componentsByStep: Record<WelcompeStepEnum, ReactElement> = {
        HOME: <DisplayHasAccount />,
        HOME_DETAILS: <DisplayStudyDetails />,
        PROFILE_SELECTOR: <DisplayProfileSelector />,
        HAS_ACCOUNT: <DisplayHasAccount />,
        ON_BOARDING: <OnBoarding />,
        LOGIN: <DisplayLogin />,
        CONSENT: (
          <DisplayConsent
            setConsent={setConsent}
            onNextClick={(cons) => onStratification(stratification, cons)}
            consentId={consentId}
          />
        ),
        INCLUSION: (
          <DisplayInclusion setInclusionContentId={setInclusionContentId} />
        ),
        INSCRIPTION: (
          <DisplayInscription
            onNextClick={(nextClickdata) => handleNextInscription(nextClickdata)}
            saveUserInfos={setUserInfo}
            userInfo={userInfo}
          />
        ),
        STRATIFICATION: (
          <StratificationPage
            onNextClick={async (strats) => setStratification(strats)}
          />
        ),
        SITES: <DisplaySites />,
        NOT_INCLUDED: (
          <DisplayNotIncluded 
            contentId={inclusionContentId}
            onTryTheApp={() => setCurrentStep(WelcompeStepEnum.INSCRIPTION)} 
          />
        ),
        REGISTRED_SUCCESSFULLY: <DisplayRegistredSuccessfully />,
        INVITATION: <DisplayInvitation />,
        INIT_IDENTIFICATION: <DisplayInitIdentification />,
      };

      return componentsByStep[currentStep];
    },
    [consent, currentStep, handleNextInscription, handlePreStratification, inclusionContentId, onStratification, userInfo]
  );

  if (isLoading) return <ResonanceLoader />;
  if (loading) return <ResonanceLoader />;

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );

  return (
    <ResonanceLayoutProvider initialLayoutConfig={{ welcomePage: true, title: "" }}>
    <ResonanceLayout>
    <HomeContextProvider
      value={{
        welcomeData,
        selectedRole,
        previousStep,
        currentStep,
        goPrevious,
        setCurrentStep,
        createUser,
      }}
    >
      {stepContent}
      <ResetPwdModal
        isOpen={isResetPwdModalOpen}
        onDismiss={() => {
          setIsResetPwdModalOpen(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
        login={login}
        channel= {channel}
        reset_password_token={forget_password_token}
      />
    </HomeContextProvider>
    </ResonanceLayout>
  </ResonanceLayoutProvider>

  );
};

export default HomeContent;
